import { Armchair, ChevronDown, ChevronUp, Clock, X } from "lucide-mui"
import { type DateTime } from "luxon"
import React, { useState } from "react"

import i18n from "@/i18n"

import { type Table } from "../shared"

import { TS_STYLES } from "./styles"
import TableDropdownPanel from "./table_dropdown_panel"
import TimeDropdownPanel from "./time_dropdown_panel"
import { type SelectionDimension } from "./use_mobile_table_selection"

type SelectionPillsProps = {
  leftDim: SelectionDimension
  rightDim: SelectionDimension
  hasTime: boolean
  hasTable: boolean
  hasConflict: boolean
  // time display
  selectedTimeRange: string | null
  durationLabel: string | null
  maxDurationLabel: string | null
  // table display
  tableName: string | null
  tableSeats: number | null
  availableCount: number
  // table dropdown data
  tables: Table[]
  availableTableIds: Set<string>
  // time dropdown picker data
  timeSlots: [string, DateTime][]
  selectedTableBusyBlocks: { startTime: string; endTime: string }[]
  closedUntil: DateTime | null
  openingRange?: { opensAt: DateTime; closesAt: DateTime } | null
  minReservationTime?: number | null
  maxReservationTime?: number | null
  // actions
  onClearTime: () => void
  onClearTable: () => void
  onSelectTable: (tableId: string) => void
  onEditTime: (startTime: string, endTime: string) => void
  startTime: string
  endTime: string
}

/**
 * The two bottom pills, obeying "gravity-left": the first-chosen dimension is a filled pill on the
 * left, the still-missing dimension is a dropdown pill on the right. Filled pills carry a `×` that
 * clears that dimension; dropdown pills open their panel (table list here; time picker is Unit 6).
 */
const SelectionPills: React.FC<SelectionPillsProps> = ({
  leftDim,
  rightDim,
  hasTime,
  hasTable,
  hasConflict,
  selectedTimeRange,
  durationLabel,
  maxDurationLabel,
  tableName,
  tableSeats,
  availableCount,
  tables,
  availableTableIds,
  timeSlots,
  selectedTableBusyBlocks,
  closedUntil,
  openingRange,
  minReservationTime,
  maxReservationTime,
  onClearTime,
  onClearTable,
  onSelectTable,
  onEditTime,
  startTime,
  endTime,
}) => {
  const [openPanel, setOpenPanel] = useState<SelectionDimension | null>(null)

  const closePanel = () => setOpenPanel(null)
  const togglePanel = (dim: SelectionDimension) => setOpenPanel((prev) => (prev === dim ? null : dim))

  const handleSelectTable = (tableId: string) => {
    onSelectTable(tableId)
    closePanel()
  }

  const renderTimePicker = (isOpen: boolean) =>
    isOpen ? (
      <TimeDropdownPanel
        startTime={startTime}
        endTime={endTime}
        timeSlots={timeSlots}
        busyBlocks={selectedTableBusyBlocks}
        closedUntil={closedUntil}
        openingOpensAt={openingRange?.opensAt ?? null}
        openingClosesAt={openingRange?.closesAt ?? null}
        minReservationTime={minReservationTime}
        maxReservationTime={maxReservationTime}
        onEditTime={onEditTime}
        onClose={closePanel}
      />
    ) : null

  const renderTimePill = () => {
    const isOpen = openPanel === "time"

    // Filled time pill: the body toggles the SAME start/end picker (Figma 1283-13709). While open it
    // gains the ring and the trailing swaps the clear-× for a chevron-up; closed, the × clears.
    if (hasTime) {
      return (
        <div className="relative flex flex-1">
          <div
            className={`${TS_STYLES.pillFilled} ${hasConflict ? TS_STYLES.pillConflict : ""} ${
              isOpen ? TS_STYLES.pillFilledOpen : ""
            }`}
            data-conflict={hasConflict ? "true" : undefined}
            data-testid="public-reservation-modal-mobile-pill-time"
          >
            <button
              type="button"
              className={`${TS_STYLES.pillFilledBody} text-left`}
              onClick={() => togglePanel("time")}
              aria-expanded={isOpen}
              data-testid="public-reservation-modal-mobile-pill-time-edit"
            >
              <Clock className={TS_STYLES.pillFilledIcon} />
              <div className="flex flex-col justify-center overflow-hidden">
                <span className={TS_STYLES.pillFilledHeadline}>{selectedTimeRange}</span>
                {durationLabel ? <span className={TS_STYLES.pillFilledSub}>{durationLabel}</span> : null}
              </div>
            </button>
            {isOpen ? (
              <button
                type="button"
                className={TS_STYLES.pillTrailing}
                aria-label={i18n.t("app.public.venue.modal.clear_time_selection")}
                onClick={closePanel}
                data-testid="public-reservation-modal-mobile-pill-time-collapse"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                className={TS_STYLES.pillTrailing}
                aria-label={i18n.t("app.public.venue.modal.clear_time_selection")}
                onClick={onClearTime}
                data-testid="public-reservation-modal-mobile-pill-time-clear"
              >
                <span className={TS_STYLES.pillTrailingBadge}>
                  <X className="h-3.5 w-3.5" />
                </span>
              </button>
            )}
          </div>
          {renderTimePicker(isOpen)}
        </div>
      )
    }

    return (
      <div className="relative flex flex-1">
        <button
          type="button"
          className={`${TS_STYLES.pillDropdown} ${isOpen ? TS_STYLES.pillDropdownOpen : ""}`}
          onClick={() => togglePanel("time")}
          aria-expanded={isOpen}
          data-testid="public-reservation-modal-mobile-pill-time-dropdown"
        >
          <div className={TS_STYLES.pillDropdownBody}>
            <Clock className={TS_STYLES.pillDropdownIcon} />
            <div className="flex flex-col justify-center overflow-hidden text-left">
              <span className={TS_STYLES.pillDropdownHeadline}>
                {i18n.t("app.public.venue.table_selection.select_time")}
              </span>
              {maxDurationLabel ? (
                <span className={TS_STYLES.pillDropdownSub}>
                  {i18n.t("app.public.venue.table_selection.max_duration", { hours: maxDurationLabel })}
                </span>
              ) : null}
            </div>
          </div>
          <span className={TS_STYLES.pillDropdownTrailing} style={isOpen ? { color: "#2596fa" } : undefined}>
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        </button>
        {renderTimePicker(isOpen)}
      </div>
    )
  }

  const renderTablePill = () => {
    if (hasTable) {
      return (
        <div
          className={`${TS_STYLES.pillFilled} ${hasConflict ? TS_STYLES.pillConflict : ""}`}
          data-conflict={hasConflict ? "true" : undefined}
          data-testid="public-reservation-modal-mobile-pill-table"
        >
          <div className={TS_STYLES.pillFilledBody}>
            <Armchair className={TS_STYLES.pillFilledIcon} />
            <div className="flex flex-col justify-center overflow-hidden">
              <span className={TS_STYLES.pillFilledHeadline}>{tableName}</span>
              {tableSeats ? (
                <span className={TS_STYLES.pillFilledSub}>
                  {i18n.t("app.public.venue.table_selection.seats_up_to", { count: tableSeats })}
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className={TS_STYLES.pillTrailing}
            aria-label={i18n.t("app.public.venue.modal.clear_table_selection")}
            onClick={onClearTable}
            data-testid="public-reservation-modal-mobile-pill-table-clear"
          >
            <span className={TS_STYLES.pillTrailingBadge}>
              <X className="h-3.5 w-3.5" />
            </span>
          </button>
        </div>
      )
    }

    const isOpen = openPanel === "table"
    return (
      <div className="relative flex flex-1">
        <button
          type="button"
          className={`${TS_STYLES.pillDropdown} ${isOpen ? TS_STYLES.pillDropdownOpen : ""}`}
          onClick={() => togglePanel("table")}
          aria-expanded={isOpen}
          data-testid="public-reservation-modal-mobile-pill-table-dropdown"
        >
          <div className={TS_STYLES.pillDropdownBody}>
            <Armchair className={TS_STYLES.pillDropdownIcon} />
            <div className="flex flex-col justify-center overflow-hidden text-left">
              <span className={TS_STYLES.pillDropdownHeadline}>
                {i18n.t("app.public.venue.table_selection.choose_your_table")}
              </span>
              <span className={TS_STYLES.pillDropdownSub}>
                {i18n.t("app.public.venue.table_selection.available_count", { count: availableCount })}
              </span>
            </div>
          </div>
          <span className={TS_STYLES.pillDropdownTrailing} style={isOpen ? { color: "#2596fa" } : undefined}>
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        </button>
        {isOpen ? (
          <TableDropdownPanel tables={tables} availableTableIds={availableTableIds} onSelect={handleSelectTable} />
        ) : null}
      </div>
    )
  }

  const renderPill = (dim: SelectionDimension) => (dim === "time" ? renderTimePill() : renderTablePill())

  return (
    <div className={TS_STYLES.pillsRow} data-testid="public-reservation-modal-mobile-pills">
      {renderPill(leftDim)}
      {renderPill(rightDim)}
    </div>
  )
}

export default SelectionPills
